/* eslint-disable max-lines */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet } from '@/components/ui/sheet';
import { useSheetContext } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/SheetContext';
import { BenachrichtigungenTrigger } from '@/domain/navigation/benachrichtigungen/components/desktop/BenachrichtigungenTrigger';
import { DropdownMenuItem } from '@/domain/navigation/benachrichtigungen/components/desktop/dropdownMenuSub/DropdownMenuItem';
import { DropdownMenuItemEmpty } from '@/domain/navigation/benachrichtigungen/components/desktop/dropdownMenuSub/DropdownMenuItemEmpty';
import { DropdownMenuSubContent } from '@/domain/navigation/benachrichtigungen/components/desktop/dropdownMenuSub/DropdownMenuSubContent';
import { DropdownMenuSubTrigger } from '@/domain/navigation/benachrichtigungen/components/desktop/dropdownMenuSub/DropdownMenuSubTrigger';
import AbwesenheitsantragDetail from '@/domain/navigation/benachrichtigungen/components/details/abwesenheitsantrag/AbwesenheitsantragDetail';
import DokumenteDetail from '@/domain/navigation/benachrichtigungen/components/details/dokumente/DokumenteDetail';
import FehlendeArbeitszeitDetail from '@/domain/navigation/benachrichtigungen/components/details/fehlendeArbeitszeit/FehlendeArbeitszeitDetail';
import FristenDetail from '@/domain/navigation/benachrichtigungen/components/details/fristen/FristenDetail';
import KrankmeldungenDetail from '@/domain/navigation/benachrichtigungen/components/details/krankmeldungen/KrankmeldungenDetail';
import PersonalfragebogenDetail from '@/domain/navigation/benachrichtigungen/components/details/personalfragebogen/PersonalfragebogenDetail';
import SchichttauschDetail from '@/domain/navigation/benachrichtigungen/components/details/schichttausch/SchichttauschDetail';
import { Benachrichtigungen } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { useProfile } from '@/hooks';
import {
  faAlarmExclamation,
  faArrowRightArrowLeft,
  faCalendarXmark,
  faClock,
  faFolder,
  faPaperPlane,
  faSuitcaseMedical,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBenachrichtigungenCount } from '@/domain/navigation/benachrichtigungen/utils/getBenachrichtigungenCount';

// eslint-disable-next-line max-lines-per-function, complexity
export function BenachrichtigungenDesktop({
  benachrichtigungen,
}: {
  benachrichtigungen: Benachrichtigungen;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { manager } = useProfile();
  const { ctx } = useSheetContext();
  const ContentComponent = ctx.content?.contentComponent;

  const count = getBenachrichtigungenCount(benachrichtigungen, manager);

  return (
    <Sheet
      open={ctx.open}
      onOpenChange={(open) => {
        ctx.setOpen(open);
      }}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <BenachrichtigungenTrigger count={count} active={count > 0} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-neutrals-100 shadow-md rounded-md flex flex-col items-center w-72 p-1 m-2">
          {!(count > 0) ? (
            <DropdownMenuItemEmpty />
          ) : (
            <>
              {manager.rechte.arbeitszeit.erlaubt &&
                manager.rechte.arbeitszeit.bearbeiten &&
                benachrichtigungen.fehlendeArbeitszeiten.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      count={benachrichtigungen.fehlendeArbeitszeiten.length}>
                      <FontAwesomeIcon icon={faClock} />
                      <p>
                        {t(
                          'domain.navigation.benachrichtigungen.fehlendeArbeitszeiten',
                        )}
                      </p>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {benachrichtigungen.fehlendeArbeitszeiten.map(
                          (benachrichtigung, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              onClick={() => setOpen(false)}
                              isLast={
                                benachrichtigungen.fehlendeArbeitszeiten
                                  .length -
                                  1 ===
                                idx
                              }>
                              <FehlendeArbeitszeitDetail
                                benachrichtigung={benachrichtigung}
                              />
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}

              {manager.rechte.kalender &&
                benachrichtigungen.abwesenheitsantraege.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      count={benachrichtigungen.fehlendeArbeitszeiten.length}>
                      <FontAwesomeIcon icon={faClock} />
                      <p>
                        {t(
                          'domain.navigation.benachrichtigungen.fehlendeArbeitszeiten',
                        )}
                      </p>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {benachrichtigungen.fehlendeArbeitszeiten.map(
                          (benachrichtigung, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              onClick={() => setOpen(false)}
                              isLast={
                                benachrichtigungen.fehlendeArbeitszeiten
                                  .length -
                                  1 ===
                                idx
                              }>
                              <FehlendeArbeitszeitDetail
                                benachrichtigung={benachrichtigung}
                              />
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}

              {manager.rechte.kalender &&
                benachrichtigungen.abwesenheitsantraege.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      count={benachrichtigungen.abwesenheitsantraege.length}>
                      <FontAwesomeIcon icon={faCalendarXmark} />
                      <p>
                        {t(
                          'domain.navigation.benachrichtigungen.abwesenheitsantraege',
                        )}
                      </p>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {benachrichtigungen.abwesenheitsantraege.map(
                          (benachrichtigung, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              onClick={() => setOpen(false)}
                              isLast={
                                benachrichtigungen.abwesenheitsantraege.length -
                                  1 ===
                                idx
                              }>
                              <AbwesenheitsantragDetail
                                benachrichtigung={benachrichtigung}
                              />
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
              {manager.rechte.kalender &&
                benachrichtigungen.krankmeldungen.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      count={benachrichtigungen.krankmeldungen.length}>
                      <FontAwesomeIcon icon={faSuitcaseMedical} />
                      <p>
                        {t(
                          'domain.navigation.benachrichtigungen.krankmeldungen.title',
                        )}
                      </p>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {benachrichtigungen.krankmeldungen.map(
                          (benachrichtigung, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              onClick={() => setOpen(false)}
                              isLast={
                                benachrichtigungen.krankmeldungen.length - 1 ===
                                idx
                              }>
                              <KrankmeldungenDetail
                                benachrichtigung={benachrichtigung}
                              />
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
              {manager.rechte.mitarbeiter.fristen &&
                benachrichtigungen.fristen.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      count={benachrichtigungen.fristen.length}>
                      <FontAwesomeIcon icon={faAlarmExclamation} />
                      <p>{t('domain.navigation.benachrichtigungen.fristen')}</p>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        {benachrichtigungen.fristen.map(
                          (benachrichtigung, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              isLast={
                                benachrichtigungen.fristen.length - 1 === idx
                              }>
                              <FristenDetail
                                benachrichtigung={benachrichtigung}
                              />
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
            </>
          )}
          {manager.rechte.mitarbeiter.dokumente &&
            benachrichtigungen.dokumente.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  count={benachrichtigungen.dokumente.length}>
                  <FontAwesomeIcon icon={faFolder} />
                  <p>{t('domain.navigation.benachrichtigungen.dokumente')}</p>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {benachrichtigungen.dokumente.map(
                      (benachrichtigung, idx) => (
                        <DropdownMenuItem
                          key={idx}
                          isLast={
                            benachrichtigungen.dokumente.length - 1 === idx
                          }>
                          <DokumenteDetail
                            benachrichtigung={benachrichtigung}
                          />
                        </DropdownMenuItem>
                      ),
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
          {manager.rechte.mitarbeiter.erlaubt &&
            benachrichtigungen.personalfragebogen.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  count={benachrichtigungen.personalfragebogen.length}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.personalfragebogen',
                    )}
                  </p>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {benachrichtigungen.personalfragebogen.map(
                      (benachrichtigung, idx) => (
                        <DropdownMenuItem
                          key={benachrichtigung.id}
                          isLast={
                            benachrichtigungen.personalfragebogen.length - 1 ===
                            idx
                          }>
                          <PersonalfragebogenDetail
                            benachrichtigung={benachrichtigung}
                          />
                        </DropdownMenuItem>
                      ),
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
          {manager.rechte.dienstplan.mitarbeiterEinteilen &&
            benachrichtigungen.schichttauschAntraege.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  count={benachrichtigungen.schichttauschAntraege.length}>
                  <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                  <p>
                    {t(
                      'domain.navigation.benachrichtigungen.schichttauschAntraege.title',
                    )}
                  </p>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {benachrichtigungen.schichttauschAntraege.map(
                      (benachrichtigung, idx) => (
                        <DropdownMenuItem
                          key={benachrichtigung.id}
                          isLast={
                            benachrichtigungen.schichttauschAntraege.length -
                              1 ===
                            idx
                          }>
                          <SchichttauschDetail
                            benachrichtigung={benachrichtigung}
                          />
                        </DropdownMenuItem>
                      ),
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
      {ContentComponent ? <ContentComponent {...ctx.content?.props} /> : null}
    </Sheet>
  );
}
